// Reportes de PowerBI del usuario (GET /api/powerbi-embed, la misma llamada y la misma forma que
// services/powerbiService.getAvailableReports, pero sin console.error: con varios usuarios de prueba el backend
// responde 429 y ese ruido ensuciaba la consola de TODOS los módulos; aquí el error se devuelve), con UNA sola
// petición por sesión: el shell la usa para decidir si «Analytics Dashboards» aparece en el menú (la de siempre lo
// escondía sin reportes, sidebarmenu.jsx:1296) y el módulo de dashboards la reutiliza para su menú y su página.
//   const { reportes, organizacion, rol, cargando, error, recargar } = useReportesPowerBI();
// El estado es UNO para toda la plataforma (useSyncExternalStore): si la lista falló al entrar y la página de
// dashboards la recupera con «Reintentar», el menú también se entera (antes cada componente tenía su copia).
// Si falla, se reintenta sola una vez a los 30 s (el 429 del límite por usuario es pasajero): sin eso el menú
// escondería «Analytics Dashboards» hasta recargar la página.
// Dueño: shell. Archivo libre para cualquier módulo.
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { API_URL, useConfigUsuario } from '../app/ConfigUsuario';

const cache = new Map(); // username → Promise<{ reports, organization, role }>
const REINTENTO_MS = 30000;

export function obtenerReportesPowerBI(username, { forzar = false } = {}) {
  if (!username) return Promise.resolve({ reports: [] });
  if (forzar || !cache.has(username)) {
    const p = fetch(`${API_URL}/api/powerbi-embed?username=${encodeURIComponent(username)}`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (r) => {
        const datos = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(datos.message || `Error al obtener reportes: ${r.status}`);
        return { reports: datos.reports || [], organization: datos.organization || null, role: datos.role || null };
      })
      .catch((e) => {
        cache.delete(username); // el siguiente intento vuelve a pedirlo
        throw e;
      });
    cache.set(username, p);
  }
  return cache.get(username);
}

let estado = { usuario: null, reportes: null, organizacion: null, rol: null, cargando: false, error: null };
const suscriptores = new Set();
let reintento = null;

function emitir(cambio) {
  estado = { ...estado, ...cambio };
  suscriptores.forEach((f) => f());
}

function suscribir(f) {
  suscriptores.add(f);
  return () => suscriptores.delete(f);
}

/** Pide la lista (una vez por usuario; `forzar` vuelve a pedirla). La comparten el menú y el módulo. */
export function cargarReportesPowerBI(usuario, { forzar = false, automatico = false } = {}) {
  if (!usuario) return;
  const mismo = estado.usuario === usuario;
  if (!forzar && mismo && (estado.reportes || estado.cargando)) return;
  clearTimeout(reintento);
  emitir({ usuario, cargando: true, error: null, ...(mismo ? {} : { reportes: null, organizacion: null, rol: null }) });
  obtenerReportesPowerBI(usuario, { forzar })
    .then((d) => {
      if (estado.usuario !== usuario) return;
      emitir({ reportes: d.reports || [], organizacion: d.organization || null, rol: d.role || null, cargando: false, error: null });
    })
    .catch((e) => {
      if (estado.usuario !== usuario) return;
      emitir({ cargando: false, error: e.message || 'Error al obtener reportes' });
      if (!automatico) reintento = setTimeout(() => cargarReportesPowerBI(usuario, { forzar: true, automatico: true }), REINTENTO_MS);
    });
}

const SIN_DATOS = { reportes: null, organizacion: null, rol: null, cargando: true, error: null };
const INACTIVO = { ...SIN_DATOS, cargando: false };

/** { reportes, organizacion, rol, cargando, error, recargar }. `activo: false` no pide nada (p. ej. sin el módulo). */
export function useReportesPowerBI({ activo = true } = {}) {
  const { username } = useConfigUsuario();
  const actual = useSyncExternalStore(suscribir, () => estado);
  useEffect(() => { if (activo) cargarReportesPowerBI(username); }, [activo, username]);
  const recargar = useCallback(() => cargarReportesPowerBI(username, { forzar: true }), [username]);
  const propio = actual.usuario === username ? actual : (activo ? SIN_DATOS : INACTIVO);
  return {
    reportes: propio.reportes,
    organizacion: propio.organizacion,
    rol: propio.rol,
    cargando: propio.cargando,
    error: propio.error,
    recargar,
  };
}
